import React, { useState, useEffect } from "react";
import { Alert, Spinner, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import RIcon from "./components/ReservasIcons";
import MarketingOptIn from "./components/MarketingOptIn";
import { SucursalBanner, SucursalCurrent, SucursalPicker, SucursalesSection } from "./components/Sucursales";
import { backTarget } from "./components/sucursalesModel";
import {
	HoldCountdown,
	SingleDatePickerPill,
	QtySelect,
	COUNTRIES,
	FALLBACK_IMAGE,
	WEEK_DAYS_ES,
	resolveUrl,
} from "./components/ReservaShared";
import dayjs from "dayjs";
import { buildLocationMapQuery } from "../canchas/components/mapConfig";
import { calculateBarCapacity } from "./components/barCapacity";
import ReviewsSection from "./components/ReviewsSection";
import WaitlistJoin from "../listaDeEspera/WaitlistJoin";
import { isWaitlistEnabled } from "../listaDeEspera/waitlistModel";
import { ONLINE_BOOKING_DISABLED_MESSAGE, isOnlineBookingDisabled } from "./onlineBooking";

const STEP_BAR = "bar";
const STEP_SLOTS = "slots";
const STEP_FORM = "form";

/**
 * Desktop reservation wizard (≥769px). Receives the shared `flow` object
 * from useReservationFlow and renders the original 3-step layout unchanged.
 */
export default function BarDetailDesktop({ flow, heroBar = null, sedes = null }) {
	const navigate = useNavigate();
	const { sucursales = [], casaCentral = null } = flow;
	const [activeTab, setActiveTab] = useState(sucursales.length > 0 ? "sucursales" : "desc");

	// Local step state — initialized from restored session or fresh start
	const [step, setStep] = useState(() => {
		if (flow.restoredStep === "form") return STEP_FORM;
		if (flow.restoredStep === "slots") return STEP_SLOTS;
		return STEP_BAR;
	});

	// Track active tab using IntersectionObserver as the user scrolls
	useEffect(() => {
		if (step !== STEP_BAR || !flow.bar) return;
		const sections = ["sec-desc", "sec-sucursales", "sec-fotos", "sec-ubi", "sec-detalles", "sec-resenas"];
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const id = entry.target.id;
						if (id === "sec-desc") setActiveTab("desc");
						else if (id === "sec-sucursales") setActiveTab("sucursales");
						else if (id === "sec-fotos") setActiveTab("fotos");
						else if (id === "sec-ubi") setActiveTab("ubi");
						else if (id === "sec-detalles") setActiveTab("detalles");
						else if (id === "sec-resenas") setActiveTab("resenas");
					}
				});
			},
			{
				rootMargin: "-20% 0px -60% 0px",
				threshold: 0,
			},
		);

		sections.forEach((id) => {
			const el = document.getElementById(id);
			if (el) observer.observe(el);
		});

		return () => {
			observer.disconnect();
		};
	}, [step, flow.bar, flow.bar?.galleryimage]);

	// If restoredStep changes after mount (bar loads async), sync once
	useEffect(() => {
		if (flow.restoredStep === "form") setStep(STEP_FORM);
		else if (flow.restoredStep === "slots") setStep(STEP_SLOTS);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flow.restoredStep]);

	const {
		bar,
		selectedDate,
		setSelectedDate,
		partySize,
		setPartySize,
		isEditingContext,
		setIsEditingContext,
		minDate,
		maxDate,
		dateLabel,
		availability,
		loadingAvail,
		availError,
		loadAvailability,
		availableSlotsData,
		availableZonesForSlot,
		selectedSlot,
		setSelectedSlot,
		selectedZone,
		setSelectedZone,
		holdExpiresAt,
		holdLoading,
		holdError,
		handleAdvanceToForm,
		handleHoldExpired,
		register,
		handleSubmit,
		errors,
		trigger,
		submitting,
		submitError,
		consent,
		setConsent,
		marketingOptIn,
		setMarketingOptIn,
		onSubmit,
		selectedCountry,
		setSelectedCountry,
		countryDropdownOpen,
		setCountryDropdownOpen,
		countryRef,
		showPaymentModal,
		setShowPaymentModal,
		modalCard,
		setModalCard,
		cardOptions,
		selectedCardOption,
		pendingValues,
		setPendingValues,
		handleConfirmPaymentSelection,
		displayPrice,
	} = flow;

	// Bars are bookable unless explicitly turned off (clubs are the opposite: opt-in).
	const bookingDisabled = isOnlineBookingDisabled(bar);

	const goToSlots = () => {
		if (bookingDisabled) return;
		setStep(STEP_SLOTS);
		loadAvailability(selectedDate);

		// Reset scroll on window and all potential app scroll containers
		window.scrollTo(0, 0);
		[".app-shell", ".app-content", ".reservas-root", "#root"].forEach((sel) => {
			const el = document.querySelector(sel);
			if (el) {
				el.scrollTo(0, 0);
				el.scrollTop = 0;
			}
		});
	};

	const handleHoldExpiredLocal = () => {
		handleHoldExpired();
		setStep(STEP_SLOTS);
	};

	const handleAdvanceToFormLocal = async () => {
		const result = await handleAdvanceToForm();
		// If navigate to login was triggered, result is undefined
		if (result?.reused || result?.created) {
			setStep(STEP_FORM);
		}
	};

	const onSubmitLocal = async (values) => {
		const result = await onSubmit(values);
		if (result === "slots") setStep(STEP_SLOTS);
	};

	if (!bar) return null;

	const img = bar.image || bar.mobileimage || FALLBACK_IMAGE;
	const cityName =
		bar.cityid?.name || (typeof bar.cityid === "string" ? bar.cityid : "");
	const openingHours = Array.isArray(bar.openingHours) ? bar.openingHours : [];
	const totalCapacity = calculateBarCapacity(bar.zones);
	const todayIndex = dayjs().day();
	// On a casa central the hero stays the casa's while `bar` is the chosen sede.
	const hero = heroBar || bar;
	const heroImg = hero.image || hero.mobileimage || FALLBACK_IMAGE;
	const heroCity = hero.cityid?.name || (typeof hero.cityid === "string" ? hero.cityid : "");
	const heroCapacity = calculateBarCapacity(hero.zones);

	// ================================================================
	// STEP: FORM
	// ================================================================
	if (step === STEP_FORM) {
		return (
			<div className="reservas-root">
				<div className="wrap reservar">
					<button className="crumb" onClick={() => setStep(STEP_SLOTS)}>
						<RIcon name="chevL" width={16} height={16} /> Volver a la selección
					</button>

					<div className="steps">
						<div className="step done">
							<span className="dot">
								<RIcon name="check" width={13} height={13} />
							</span>
							Elegir zona
						</div>
						<div className="step-sep done" />
						<div className="step on">
							<span className="dot">2</span>
							Tus datos
						</div>
						<div className="step-sep" />
						<div className="step">
							<span className="dot">3</span>
							{bar.reservationMode === "deposit" ? "Pago" : "Confirmar"}
						</div>
					</div>

					{holdExpiresAt && (
						<HoldCountdown
							expiresAt={holdExpiresAt}
							onExpired={handleHoldExpiredLocal}
						/>
					)}

					<div className="reservar-grid">
						<div className="form-card">
							<h3>Completá tus datos</h3>
							<p className="fc-sub">
								Usamos estos datos para confirmar tu reserva y enviarte el
								comprobante.
							</p>

							<form onSubmit={handleSubmit(onSubmitLocal)}>
								<div className="fgrid">
									<div className="field full">
										<label>
											Nombre y apellido <span className="req">*</span>
										</label>
										<div className="inp">
											<RIcon name="user" />
											<input
												placeholder="Ej. Camila Rodríguez"
												{...register("name", {
													required: "Ingresá tu nombre.",
												})}
											/>
										</div>
										{errors.name && (
											<span style={{ fontSize: 12, color: "#e53935" }}>
												{errors.name.message}
											</span>
										)}
									</div>
									<div className="field">
										<label>
											Email <span className="req">*</span>
										</label>
										<div className="inp">
											<RIcon name="mail" />
											<input
												type="email"
												placeholder="tu@email.com"
												{...register("email", {
													required: "Ingresá un email válido.",
													pattern: {
														value: /^\S+@\S+\.\S+$/,
														message: "Email inválido.",
													},
												})}
											/>
										</div>
										<span className="hint">
											Te enviamos el comprobante acá.
										</span>
										{errors.email && (
											<span style={{ fontSize: 12, color: "#e53935" }}>
												{errors.email.message}
											</span>
										)}
									</div>
									<div className="field">
										<label>
											Teléfono <span className="req">*</span>
										</label>
										<div
											className={`inp${errors.phone ? " is-invalid" : ""}`}
											style={{
												paddingLeft: 0,
												position: "relative",
												borderColor: errors.phone ? "#e53935" : undefined,
												boxShadow: errors.phone
													? "0 0 0 3px rgba(229,57,53,0.18)"
													: undefined,
											}}
										>
											<div
												ref={countryRef}
												style={{
													display: "flex",
													alignItems: "center",
													gap: "8px",
													paddingLeft: "14px",
													paddingRight: "12px",
													height: "100%",
													background: "#f9fafb",
													borderTopLeftRadius: "9px",
													borderBottomLeftRadius: "9px",
													userSelect: "none",
													borderRight: "1px solid var(--zoco-border, #E5E7EB)",
													flexShrink: 0,
													cursor: "pointer",
													position: "relative",
												}}
												onClick={() => setCountryDropdownOpen((o) => !o)}
											>
												{selectedCountry.flag}
												<span
													style={{
														fontSize: "13.5px",
														fontWeight: "700",
														color: "#2d3035",
														whiteSpace: "nowrap",
														fontFamily: "var(--zoco-font, inherit)",
													}}
												>
													{selectedCountry.label}
												</span>
												<svg
													width="8"
													height="5"
													viewBox="0 0 8 5"
													fill="none"
													style={{
														marginLeft: "2px",
														transform: countryDropdownOpen
															? "rotate(180deg)"
															: "rotate(0deg)",
														transition: "transform 0.2s",
													}}
												>
													<path
														d="M1 1L4 4L7 1"
														stroke="#4b5563"
														strokeWidth="1.5"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
												{countryDropdownOpen && (
													<div
														style={{
															position: "absolute",
															top: "calc(100% + 6px)",
															left: 0,
															background: "#ffffff",
															border: "1px solid #E5E7EB",
															borderRadius: "10px",
															boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
															zIndex: 1000,
															minWidth: "160px",
															padding: "6px 0",
															maxHeight: "200px",
															overflowY: "auto",
														}}
													>
														{COUNTRIES.map((c) => (
															<button
																key={c.code}
																type="button"
																onClick={(e) => {
																	e.stopPropagation();
																	setSelectedCountry(c);
																	setCountryDropdownOpen(false);
																	setTimeout(() => {
																		trigger("phone");
																	}, 0);
																}}
																style={{
																	display: "flex",
																	alignItems: "center",
																	width: "100%",
																	padding: "8px 14px",
																	border: "none",
																	background:
																		selectedCountry.code === c.code
																			? "#f3f4f6"
																			: "#ffffff",
																	cursor: "pointer",
																	gap: "10px",
																	textAlign: "left",
																}}
															>
																{c.flag}
																<span
																	style={{
																		fontSize: "13px",
																		fontWeight: "600",
																		color: "#2d3035",
																		flex: 1,
																	}}
																>
																	{c.label}
																</span>
																{selectedCountry.code === c.code && (
																	<span
																		style={{
																			color: "#00b159",
																			fontWeight: "bold",
																			fontSize: "14px",
																		}}
																	>
																		✓
																	</span>
																)}
															</button>
														))}
													</div>
												)}
											</div>
											<input
												type="tel"
												placeholder={selectedCountry.placeholder}
												style={{
													border: "none",
													outline: "none",
													height: "100%",
													width: "100%",
													paddingLeft: "12px",
													fontSize: "14.5px",
													fontWeight: "600",
													color: "var(--zoco-ink, #2d3035)",
													background: "transparent",
												}}
												{...register("phone", {
													required: "Ingresá tu teléfono.",
													validate: (val) => {
														return (
															selectedCountry.validate(val) ||
															"Este número no es válido"
														);
													},
												})}
											/>
										</div>
										<span className="hint">
											Por si el bar necesita contactarte.
										</span>
										{errors.phone && (
											<span
												style={{
													fontSize: 12,
													color: "#e53935",
													marginTop: "4px",
													display: "block",
												}}
											>
												{errors.phone.message}
											</span>
										)}
									</div>
									<div className="field">
										<label>
											DNI <span className="req">*</span>
										</label>
										<div className="inp">
											<RIcon name="user" />
											<input
												placeholder="Tu DNI"
												onInput={(e) => {
													e.target.value = (e.target.value || "")
														.replace(/\D/g, "")
														.slice(0, 10);
												}}
												{...register("dni", {
													required: "Ingresá tu DNI.",
													pattern: {
														value: /^\d{7,10}$/,
														message:
															"DNI inválido (debe tener entre 7 y 10 números).",
													},
												})}
											/>
										</div>
										{errors.dni && (
											<span style={{ fontSize: 12, color: "#e53935" }}>
												{errors.dni.message}
											</span>
										)}
									</div>
									<div className="field full">
										<label>Notas adicionales</label>
										<div
											className="inp"
											style={{ height: "auto", padding: "12px 14px" }}
										>
											<textarea
												placeholder="Cumpleaños, alergias, preferencias…"
												rows={2}
												style={{
													border: "none",
													outline: "none",
													width: "100%",
													resize: "none",
													fontFamily: "inherit",
													fontSize: 14,
												}}
												{...register("notes")}
											/>
										</div>
									</div>
								</div>

								<div className="reservation-details-box">
									<div className="rdb-title">Detalles de la reserva</div>
									<div className="rdb-row">
										<span>
											<RIcon name="pin" width={14} height={14} /> Zona
										</span>
										<strong>{selectedZone}</strong>
									</div>
									<div className="rdb-row">
										<span>
											<RIcon name="cal" width={14} height={14} /> Fecha
										</span>
										<strong>{selectedDate.format("DD/MM/YYYY")}</strong>
									</div>
									<div className="rdb-row">
										<span>
											<RIcon name="clock" width={14} height={14} /> Horario
										</span>
										<strong>{selectedSlot?.slotStart} hs</strong>
									</div>
									<div className="rdb-row">
										<span>
											<RIcon name="users" width={14} height={14} /> Comensales
										</span>
										<strong>{partySize}</strong>
									</div>
									<div className="rdb-row">
										<span>
											<RIcon name="clock" width={14} height={14} /> Estadía máx.
										</span>
										<strong>{bar.slotDurationMinutes || 120} min</strong>
									</div>
									<div className="rdb-row">
										<span>
											<RIcon name="shield" width={14} height={14} /> Tolerancia
										</span>
										<strong>{bar.toleranceMinutes || 15} min</strong>
									</div>
									{bar.additionalNotes && (
										<div className="rdb-note">
											<RIcon name="info" width={13} height={13} />{" "}
											{bar.additionalNotes}
										</div>
									)}
								</div>

								<MarketingOptIn venueName={bar.name} checked={marketingOptIn} onChange={setMarketingOptIn} />

								<div className="consent" onClick={() => setConsent((c) => !c)}>
									<span className={`cbox${consent ? " on" : ""}`}>
										{consent && <RIcon name="check" width={13} height={13} />}
									</span>
									<p>
										Acepto la <strong>política de reservas</strong> de{" "}
										{bar.name}
										{bar.cancellationAllowed !== false &&
										bar.cancellationAllowed !== "false"
											? `: cancelación sin cargo hasta ${bar.cancellationPolicyHours || 24} hs antes.`
											: ": las reservas no son cancelables."}
									</p>
								</div>

								{submitError && (
									<Alert variant="danger" className="mt-3">
										{submitError}
									</Alert>
								)}

								<div style={{ display: "flex", gap: 12, marginTop: 24 }}>
									<button
										type="button"
										className="r-btn-soft"
										style={{ padding: "13px 22px" }}
										onClick={() => setStep(STEP_SLOTS)}
									>
										Volver
									</button>
									<button
										type="submit"
										className="r-btn-primary"
										style={{
											flex: 1,
											padding: 14,
											justifyContent: "center",
											opacity: consent ? 1 : 0.5,
										}}
										disabled={submitting || !consent}
									>
										{submitting ? (
											<Spinner animation="border" size="sm" />
										) : (
											<>
												{bar.reservationMode === "deposit"
													? "Ir al pago"
													: "Confirmar reserva"}
												<RIcon name="arrowR" width={17} height={17} />
											</>
										)}
									</button>
								</div>

								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
										marginTop: 16,
										fontSize: 12,
										fontWeight: 600,
										color: "#6b7280",
									}}
								>
									<RIcon
										name="shield"
										width={15}
										height={15}
										style={{ color: "#b1c20e" }}
									/>
									{bar.reservationMode === "deposit"
										? "Te vamos a redirigir a nuestro sistema de pago seguro para confirmar la seña."
										: "Tu reserva quedará confirmada de inmediato y recibirás un comprobante por email."}
								</div>
							</form>
						</div>

						<div className="sum-card">
							<div className="sum-img">
								<img
									src={img}
									alt={bar.name}
									onError={(e) => {
										e.currentTarget.src = FALLBACK_IMAGE;
									}}
								/>
								<div className="sc" />
								<div className="bn">{bar.name}</div>
							</div>
							<div className="sum-body">
								<h4>Resumen</h4>
								<div className="rs-row">
									<span className="k">
										<RIcon name="pin" />
										Zona
									</span>
									<span className="v">{selectedZone}</span>
								</div>
								<div className="rs-row">
									<span className="k">
										<RIcon name="users" />
										Comensales
									</span>
									<span className="v">{partySize}</span>
								</div>
								<div className="rs-row">
									<span className="k">
										<RIcon name="cal" />
										Fecha
									</span>
									<span className="v">{selectedDate.format("DD/MM/YYYY")}</span>
								</div>
								<div className="rs-row">
									<span className="k">
										<RIcon name="clock" />
										Horario
									</span>
									<span className="v">{selectedSlot?.slotStart} hs</span>
								</div>
								<div className="rs-div" />
								<div className="rs-total">
									<span className="k">Seña de reserva</span>
									<span className="v">{displayPrice || "Sin seña"}</span>
								</div>
								<div className="rs-note">
									<RIcon name="shield" width={15} height={15} />
									El precio que ves es el que pagás. Sin cargos por servicio.
								</div>
							</div>
						</div>
					</div>
				</div>

				<Modal
					show={showPaymentModal}
					onHide={() => {
						setShowPaymentModal(false);
						setPendingValues(null);
					}}
					centered
					backdrop="static"
					className="payment-modal"
				>
					<Modal.Header closeButton>
						<Modal.Title>Elegí cómo pagar</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="mb-3">
							<label className="form-label">Tarjeta</label>
							<QtySelect
								className="payment-select"
								placeholder="Elegí una tarjeta"
								value={modalCard}
								onChange={(val) => setModalCard(val == null ? "" : String(val))}
								options={(cardOptions || []).map((opt) => ({
									value: opt.id,
									label: opt.label,
									disabled: opt.disabled,
								}))}
							/>
						</div>
						{selectedCardOption && (
							<div className="mb-3">
								<label className="form-label">Cuotas</label>
								<QtySelect
									className="payment-select"
									placeholder="Elegí la cantidad de cuotas"
									value="1"
									disabled={true}
									onChange={() => {}}
									options={[{ value: "1", label: "1 cuota (sin interés)" }]}
								/>
							</div>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Button
							variant="secondary"
							onClick={() => {
								setShowPaymentModal(false);
								setPendingValues(null);
							}}
						>
							Cancelar
						</Button>
						<Button
							variant="primary"
							onClick={handleConfirmPaymentSelection}
							disabled={!selectedCardOption}
						>
							Continuar
						</Button>
					</Modal.Footer>
				</Modal>
			</div>
		);
	}

	// ================================================================
	// STEP: SLOTS
	// ================================================================
	if (step === STEP_SLOTS) {
		return (
			<div className="reservas-root">
				<div className="wrap bar-detail">
					<button className="crumb" onClick={() => setStep(STEP_BAR)}>
						<RIcon name="chevL" width={16} height={16} /> Volver al bar
					</button>

					<div className="steps" style={{ marginBottom: 24 }}>
						<div className="step done">
							<span className="dot">
								<RIcon name="check" width={13} height={13} />
							</span>
							Info del bar
						</div>
						<div className="step-sep done" />
						<div className="step on">
							<span className="dot">2</span>Elegir zona
						</div>
						<div className="step-sep" />
						<div className="step">
							<span className="dot">3</span>Tus datos
						</div>
					</div>

					<div
						className="avail-context-banner"
						style={{
							display: "flex",
							alignItems: "center",
							gap: "12px",
							flexWrap: "wrap",
							position: "relative",
						}}
					>
						{isEditingContext ? (
							<>
								<div
									style={{ display: "flex", alignItems: "center", gap: "8px" }}
								>
									<RIcon name="cal" width={14} height={14} />
									<SingleDatePickerPill
										value={selectedDate}
										onChange={(d) => {
											setSelectedDate(d);
											loadAvailability(d);
										}}
										minDate={minDate}
										maxDate={maxDate}
									/>
								</div>
								<span className="acb-sep">·</span>
								<div
									style={{ display: "flex", alignItems: "center", gap: "8px" }}
								>
									<RIcon name="users" width={14} height={14} />
									<div
										className="party-picker"
										style={{
											display: "inline-flex",
											scale: "0.85",
											padding: 0,
										}}
									>
										<button
											type="button"
											className="party-btn"
											style={{
												height: "30px",
												width: "30px",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
											onClick={() => {
												const n = Math.max(1, partySize - 1);
												setPartySize(n);
												loadAvailability(selectedDate);
											}}
											disabled={partySize <= 1}
										>
											−
										</button>
										<span
											className="party-val"
											style={{
												margin: "0 8px",
												fontSize: "14px",
												fontWeight: "700",
											}}
										>
											{partySize}
										</span>
										<button
											type="button"
											className="party-btn"
											style={{
												height: "30px",
												width: "30px",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
											onClick={() => {
												const n = Math.min(
													bar?.maxPartySize || 20,
													partySize + 1,
												);
												setPartySize(n);
												loadAvailability(selectedDate);
											}}
											disabled={partySize >= (bar?.maxPartySize || 20)}
										>
											+
										</button>
									</div>
								</div>
								<button
									type="button"
									className="r-btn-primary"
									style={{
										height: "30px",
										padding: "0 14px",
										borderRadius: "8px",
										fontSize: "12px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginLeft: "auto",
									}}
									onClick={() => setIsEditingContext(false)}
								>
									Listo
								</button>
							</>
						) : (
							<>
								<span>
									<RIcon name="cal" width={14} height={14} />{" "}
									{selectedDate.format("DD [de] MMMM [de] YYYY")}
								</span>
								<span className="acb-sep">·</span>
								<span>
									<RIcon name="users" width={14} height={14} /> {partySize}{" "}
									{partySize === 1 ? "persona" : "personas"}
								</span>
								<button
									type="button"
									className="acb-change"
									onClick={() => setIsEditingContext(true)}
								>
									Cambiar
								</button>
							</>
						)}
					</div>

					<div className="bd-grid">
						<div>
							<div className="bd-section">
								<h3 className="section-title">Elegí un horario</h3>
								{loadingAvail ? (
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: 8,
											fontSize: 13,
											color: "#6b7280",
										}}
									>
										<Spinner animation="border" size="sm" /> Cargando
										disponibilidad…
									</div>
								) : availError ? (
									<Alert variant="warning">{availError}</Alert>
								) : availableSlotsData.length === 0 ? (
									<Alert variant="light">
										No hay horarios disponibles para{" "}
										{selectedDate.format("DD/MM/YYYY")}. Probá con otra fecha.
									</Alert>
								) : (
									<div className="slots-grid">
										{availableSlotsData.map((slot) => (
											<button
												key={slot.slotStart}
												type="button"
												className={`slot-btn${selectedSlot?.slotStart === slot.slotStart ? " selected" : ""}${!slot.hasAvailability ? " unavailable" : ""}`}
												disabled={!slot.hasAvailability}
												onClick={() => {
													setSelectedSlot(slot);
													setSelectedZone(null);
												}}
											>
												<span className="slot-time">{slot.slotStart} hs</span>
												<span className="slot-end">hasta {slot.slotEnd}</span>
												{!slot.hasAvailability && (
													<span className="slot-tag">Sin lugar</span>
												)}
											</button>
										))}
									</div>
								)}
								<WaitlistJoin enabled={isWaitlistEnabled(bar)} barId={bar._id} kind="table" date={dateLabel} criteria={{ partySize: Number(partySize) }} summary={`${partySize} persona${Number(partySize) === 1 ? "" : "s"}`} />
							</div>

							{selectedSlot && (
								<div className="bd-section">
									<h3 className="section-title">Elegí una zona</h3>
									{availableZonesForSlot.length === 0 ? (
										<Alert variant="light">
											No hay zonas disponibles para ese horario.
										</Alert>
									) : (
										<div className="zones-grid">
											{(availability.zones || []).map((zone) => {
												const slotData = zone.slots?.find(
													(s) => s.slotStart === selectedSlot.slotStart,
												);
												const available = slotData?.available ?? false;
												const count = slotData?.availableCount ?? 0;
												const isSelected = selectedZone === zone.name;
												const zonePrice =
													bar.reservationMode === "deposit"
														? bar.zonePricingEnabled &&
															zone.priceEnabled &&
															zone.price > 0
															? `$${Number(zone.price).toLocaleString("es-AR")}`
															: bar.depositAmount > 0
																? `$${Number(bar.depositAmount).toLocaleString("es-AR")}`
																: null
														: null;
												return (
													<button
														key={zone.name}
														type="button"
														className={`zone-btn${isSelected ? " selected" : ""}${!available ? " unavailable" : ""}`}
														disabled={!available}
														onClick={() => setSelectedZone(zone.name)}
													>
														<span className="zone-name">{zone.name}</span>
														{available ? (
															<span className="zone-avail">
																{count} {count === 1 ? "lugar" : "lugares"}
															</span>
														) : (
															<span className="zone-tag">Sin lugar</span>
														)}
														{zonePrice && (
															<span className="zone-price">{zonePrice}</span>
														)}
													</button>
												);
											})}
										</div>
									)}
								</div>
							)}

							{holdError && <Alert variant="danger">{holdError}</Alert>}
						</div>

						<div className="bd-sidebar-sticky">
							<div className="reserve-stick">
								<div className="rs-head">
									<div className="t">Tu reserva</div>
									<div className="n">{bar.name}</div>
								</div>
								<div className="rs-body">
									{!selectedSlot ? (
										<div className="rs-empty">
											<RIcon name="glass" width={34} height={34} />
											<br />
											Elegí un horario para ver las zonas disponibles.
										</div>
									) : (
										<>
											{selectedZone && (
												<div className="rs-row">
													<span className="k">
														<RIcon name="pin" />
														Zona
													</span>
													<span className="v">{selectedZone}</span>
												</div>
											)}
											<div className="rs-row">
												<span className="k">
													<RIcon name="cal" />
													Fecha
												</span>
												<span className="v">
													{selectedDate.format("DD/MM/YYYY")}
												</span>
											</div>
											<div className="rs-row">
												<span className="k">
													<RIcon name="clock" />
													Horario
												</span>
												<span className="v">{selectedSlot.slotStart} hs</span>
											</div>
											<div className="rs-row">
												<span className="k">
													<RIcon name="users" />
													Comensales
												</span>
												<span className="v">{partySize}</span>
											</div>
											{selectedZone &&
												displayPrice &&
												(() => {
													const depositNumeric =
														Number(
															String(displayPrice).replace(/[^\d]/g, ""),
														) || 0;
													if (depositNumeric <= 0) {
														return (
															<>
																<div className="rs-div" />
																<div className="rs-total">
																	<span className="k">Seña de reserva</span>
																	<span className="v">{displayPrice}</span>
																</div>
															</>
														);
													}
													const fakeFee = Math.round(depositNumeric * 0.01);
													return (
														<>
															<div className="rs-div" />
															<div
																className="rs-row"
																style={{
																	fontSize: "13.5px",
																	background: "var(--zoco-lime-tint, #F6F9E8)",
																	padding: "6px 8px",
																	borderRadius: "6px",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "space-between",
																	margin: "4px 0",
																}}
															>
																<span
																	className="k"
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "4px",
																		color: "var(--zoco-lime-strong, #8FA80A)",
																		fontWeight: 600,
																	}}
																>
																	<RIcon name="ticket" width={14} height={14} />{" "}
																	Tasa de servicio
																</span>
																<span
																	className="v"
																	style={{ display: "flex", gap: "8px" }}
																>
																	<span
																		style={{
																			textDecoration: "line-through",
																			color: "#9ca3af",
																		}}
																	>
																		${fakeFee.toLocaleString("es-AR")}
																	</span>
																	<strong
																		style={{
																			color: "var(--zoco-lime-strong, #8FA80A)",
																		}}
																	>
																		$0
																	</strong>
																</span>
															</div>
															<div
																className="rs-note"
																style={{
																	justifyContent: "center",
																	marginTop: "8px",
																	marginBottom: "8px",
																	width: "100%",
																	display: "flex",
																	alignItems: "center",
																	gap: "6px",
																}}
															>
																<RIcon name="shield" width={15} height={15} />
																Reserva segura, sin costos extra de servicio.
															</div>
															<div className="rs-div" />
															<div className="rs-total">
																<span className="k">Seña de reserva</span>
																<span className="v">{displayPrice}</span>
															</div>
														</>
													);
												})()}
										</>
									)}
									<button
										className="r-btn-primary rs-cta"
										disabled={!selectedSlot || !selectedZone || holdLoading}
										onClick={handleAdvanceToFormLocal}
									>
										{holdLoading ? (
											<Spinner animation="border" size="sm" />
										) : (
											<>
												Continuar <RIcon name="arrowR" width={17} height={17} />
											</>
										)}
									</button>
								</div>
							</div>

							<div className="policy-box">
								{bar.cancellationAllowed === false ||
								bar.cancellationAllowed === "false" ? (
									<>
										<div className="policy">
											<RIcon name="cal" width={16} height={16} />
											Las reservas se pueden realizar hasta con{" "}
											{bar.reservationWindowDays || 6}{" "}
											{bar.reservationWindowDays === 1 ? "día" : "días"} de
											antelación.
										</div>
										<div className="policy">
											<RIcon
												name="alert"
												width={16}
												height={16}
												style={{ color: "var(--zoco-danger)" }}
											/>
											Las reservas no son cancelables.
										</div>
									</>
								) : (
									<>
										<div className="policy">
											<RIcon name="cal" width={16} height={16} />
											Las reservas se pueden realizar hasta con{" "}
											{bar.reservationWindowDays || 6}{" "}
											{bar.reservationWindowDays === 1 ? "día" : "días"} de
											antelación.
										</div>
										<div className="policy">
											<RIcon name="shield" width={16} height={16} />
											Cancelación sin cargo hasta{" "}
											{bar.cancellationPolicyHours || 24} hs antes.
										</div>
									</>
								)}
								{bar.additionalNotes && (
									<div className="policy">
										<RIcon name="info" width={16} height={16} />
										{bar.additionalNotes}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// ================================================================
	// STEP: BAR DETAIL (Info + fecha/comensales)
	// ================================================================
	return (
		<div className="reservas-root">
			<div className="wrap bar-detail">
				<button className="crumb" onClick={() => navigate(backTarget("reservas", casaCentral).path)}>
					<RIcon name="chevL" width={16} height={16} /> {backTarget("reservas", casaCentral).label}
				</button>

				<SucursalBanner kind="reservas" casaCentral={casaCentral} />

				<div className="bd-hero">
					<img
						src={heroImg}
						alt={hero.name}
						onError={(e) => {
							e.currentTarget.src = FALLBACK_IMAGE;
						}}
					/>
					<div className="bd-hero-scrim" />
					<div className="bd-hero-info">
						<h1>{hero.name}</h1>
						<div className="meta">
							{hero.address && (
								<span>
									<RIcon name="pin" width={16} height={16} /> {hero.address}
									{heroCity ? `, ${heroCity}` : ""}
								</span>
							)}
							{heroCapacity > 0 && (
								<span>
									<RIcon name="users" width={16} height={16} /> Capacidad máx.{" "}
									{heroCapacity}
								</span>
							)}
							{hero.depositAmount > 0 && (
								<span>
									<RIcon name="dollar" width={16} height={16} /> Desde $
									{Number(hero.depositAmount).toLocaleString("es-AR")}
								</span>
							)}
						</div>
					</div>
					{hero.reservationMode === "deposit" && (
						<span className="bd-pill">
							<RIcon name="shield" width={15} height={15} /> Con seña
						</span>
					)}
				</div>

				<div className="bd-grid bd-info-grid">
					<div>
						<div className="info-tabs-sticky">
							{sucursales.length > 0 && (
								<button
									type="button"
									className={`info-tab-link ${activeTab === "sucursales" ? "active" : ""}`}
									onClick={() => {
										setActiveTab("sucursales");
										document
											.getElementById("sec-sucursales")
											?.scrollIntoView({ behavior: "smooth", block: "center" });
									}}
								>
									Sucursales
								</button>
							)}
							<button
								type="button"
								className={`info-tab-link ${activeTab === "desc" ? "active" : ""}`}
								onClick={() => {
									setActiveTab("desc");
									document
										.getElementById("sec-desc")
										?.scrollIntoView({ behavior: "smooth", block: "center" });
								}}
							>
								Descripción
							</button>
							{Array.isArray(bar.galleryimage) &&
								bar.galleryimage.length > 0 && (
									<button
										type="button"
										className={`info-tab-link ${activeTab === "fotos" ? "active" : ""}`}
										onClick={() => {
											setActiveTab("fotos");
											document
												.getElementById("sec-fotos")
												?.scrollIntoView({
													behavior: "smooth",
													block: "center",
												});
										}}
									>
										Galería
									</button>
								)}
							<button
								type="button"
								className={`info-tab-link ${activeTab === "ubi" ? "active" : ""}`}
								onClick={() => {
									setActiveTab("ubi");
									document
										.getElementById("sec-ubi")
										?.scrollIntoView({ behavior: "smooth", block: "center" });
								}}
							>
								Ubicación
							</button>
							<button
								type="button"
								className={`info-tab-link ${activeTab === "detalles" ? "active" : ""}`}
								onClick={() => {
									setActiveTab("detalles");
									document
										.getElementById("sec-detalles")
										?.scrollIntoView({ behavior: "smooth", block: "center" });
								}}
							>
								Detalles
							</button>
							<button
								type="button"
								className={`info-tab-link ${activeTab === "resenas" ? "active" : ""}`}
								onClick={() => {
									setActiveTab("resenas");
									document
										.getElementById("sec-resenas")
										?.scrollIntoView({ behavior: "smooth", block: "center" });
								}}
							>
								Reseñas
							</button>
						</div>

						<SucursalesSection kind="reservas" sucursales={sucursales} id="sec-sucursales" isDesktop sedes={sedes} />

						<div id="sec-desc" className="bd-section-stacked">
							<h3 className="section-title">Descripción</h3>
							<p style={{ fontSize: 15, lineHeight: 1.7, color: "#4b5563" }}>
								{bar.description ||
									bar.shortDescription ||
									"Bar disponible para reservas."}
							</p>
							{Array.isArray(bar.amenities) && bar.amenities.length > 0 && (
								<div className="bar-tags" style={{ marginTop: 16 }}>
									{bar.amenities.map((t) => (
										<span className="bar-tag" key={t}>
											{t}
										</span>
									))}
								</div>
							)}
						</div>

						{Array.isArray(bar.galleryimage) && bar.galleryimage.length > 0 && (
							<div id="sec-fotos" className="bd-section-stacked">
								<h3 className="section-title">Galería</h3>
								<div className="gallery-layout-premium">
									{bar.galleryimage.slice(0, 5).map((url, idx) => (
										<div
											key={idx}
											className={`gallery-img-wrapper item-${idx}`}
										>
											<img
												src={resolveUrl(url)}
												alt={`${bar.name} galería ${idx + 1}`}
												onError={(e) => {
													e.currentTarget.src = FALLBACK_IMAGE;
												}}
											/>
										</div>
									))}
								</div>
							</div>
						)}

						<div id="sec-ubi" className="bd-section-stacked">
							<h3 className="section-title">Ubicación</h3>
							{bar.address && (
								<p
									className="bd-address-text"
									style={{
										fontSize: "14px",
										color: "#4b5563",
										display: "flex",
										alignItems: "center",
										gap: "6px",
										marginBottom: "12px",
									}}
								>
									<RIcon name="pin" width={16} height={16} /> {bar.address}
									{cityName ? `, ${cityName}` : ""}
								</p>
							)}
							<div
								style={{
									height: "300px",
									border: "1px solid #e5e7eb",
									borderRadius: "12px",
									overflow: "hidden",
									background: "#f3f4f6",
								}}
							>
								{(() => {
									const queryAddress = buildLocationMapQuery(bar);
									if (!queryAddress) return null;
									const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(queryAddress)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
									return (
										<iframe
											src={mapSrc}
											width="100%"
											height="100%"
											style={{ border: 0, display: "block" }}
											allowFullScreen=""
											loading="lazy"
											title="Google Maps"
										/>
									);
								})()}
							</div>
						</div>

						<div id="sec-detalles" className="bd-section-stacked">
							<h3 className="section-title">Detalles</h3>
							<div className="details-grid-premium">
								{bar.contactPhone && (
									<div className="detail-item-premium">
										<span className="icon">
											<RIcon name="phone" width={18} height={18} />
										</span>
										<div className="info">
											<span className="label">Celular</span>
											<strong className="val">{bar.contactPhone}</strong>
										</div>
									</div>
								)}
								{bar.contactEmail && (
									<div className="detail-item-premium">
										<span className="icon">
											<RIcon name="mail" width={18} height={18} />
										</span>
										<div className="info">
											<span className="label">Correo</span>
											<strong className="val">{bar.contactEmail}</strong>
										</div>
									</div>
								)}
								{bar.instagram && (
									<div className="detail-item-premium">
										<span className="icon">
											<svg
												width="18"
												height="18"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2.2"
												strokeLinecap="round"
												strokeLinejoin="round"
											>
												<rect
													x="2"
													y="2"
													width="20"
													height="20"
													rx="5"
													ry="5"
												></rect>
												<path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
												<line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
											</svg>
										</span>
										<div className="info">
											<span className="label">Instagram</span>
											<a
												href={`https://instagram.com/${bar.instagram.replace("@", "")}`}
												target="_blank"
												rel="noopener noreferrer"
												className="val-link"
											>
												@{bar.instagram.replace("@", "")}
											</a>
										</div>
									</div>
								)}
								{bar.website && (
									<div className="detail-item-premium">
										<span className="icon">
											<svg
												width="18"
												height="18"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2.2"
												strokeLinecap="round"
												strokeLinejoin="round"
											>
												<circle cx="12" cy="12" r="10"></circle>
												<line x1="2" y1="12" x2="22" y2="12"></line>
												<path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
											</svg>
										</span>
										<div className="info">
											<span className="label">Sitio Web</span>
											<a
												href={
													bar.website.startsWith("http")
														? bar.website
														: `https://${bar.website}`
												}
												target="_blank"
												rel="noopener noreferrer"
												className="val-link"
											>
												{bar.website.replace(/^https?:\/\/(www\.)?/, "")}
											</a>
										</div>
									</div>
								)}
							</div>
							<div style={{ marginTop: "24px" }}>
								<h4
									style={{
										fontSize: "14px",
										fontWeight: "700",
										color: "#2d3035",
										marginBottom: "12px",
									}}
								>
									Horarios de atención
								</h4>
								<div className="hours-list-premium">
									{openingHours.length > 0 ? (
										openingHours
											.slice()
											.sort((a, b) => a.weekday - b.weekday)
											.map((h, i) => (
												<div
													className={`hours-row-premium${h.weekday === todayIndex ? " today" : ""}${!h.enabled ? " closed" : ""}`}
													key={i}
												>
													<span className="d">{WEEK_DAYS_ES[h.weekday]}</span>
													<span className="h">
														{h.enabled ? `${h.open} – ${h.close}` : "Cerrado"}
													</span>
												</div>
											))
									) : (
										<p style={{ fontSize: 14, color: "#6b7280" }}>
											Horarios no disponibles.
										</p>
									)}
								</div>
							</div>
						</div>
						<ReviewsSection barId={bar._id} id="sec-resenas" />
					</div>

					<div className="bd-sidebar-sticky">
						<div className="reserve-stick">
							<div className="rs-head">
								<div className="t">Hacer una reserva</div>
								<div className="n">{bar.name}</div>
							</div>
							<div className="rs-body">
								<SucursalPicker kind="reservas" currentName={bar.name} sucursales={sucursales} sedes={sedes} />
								<SucursalCurrent kind="reservas" name={bar.name} casaCentral={casaCentral} />
								<div className="field" style={{ marginBottom: 12 }}>
									<label
										style={{
											fontSize: 13,
											fontWeight: 600,
											color: "#374151",
											marginBottom: 6,
											display: "block",
										}}
									>
										Fecha
									</label>
									<SingleDatePickerPill
										value={selectedDate}
										onChange={(d) => setSelectedDate(d)}
										minDate={minDate}
										maxDate={maxDate}
									/>
								</div>
								<div className="field" style={{ marginBottom: 16 }}>
									<label
										style={{
											fontSize: 13,
											fontWeight: 600,
											color: "#374151",
											marginBottom: 6,
											display: "block",
										}}
									>
										Cantidad de comensales
									</label>
									<div className="party-picker">
										<button
											type="button"
											className="party-btn"
											onClick={() => setPartySize((n) => Math.max(1, n - 1))}
											disabled={partySize <= 1}
										>
											−
										</button>
										<span className="party-val">{partySize}</span>
										<button
											type="button"
											className="party-btn"
											onClick={() =>
												setPartySize((n) =>
													Math.min(bar.maxPartySize || 20, n + 1),
												)
											}
											disabled={partySize >= (bar.maxPartySize || 20)}
										>
											+
										</button>
									</div>
								</div>
								<button
									className="r-btn-primary rs-cta"
									onClick={goToSlots}
									disabled={bookingDisabled}
									aria-disabled={bookingDisabled}
								>
									Ver disponibilidad{" "}
									<RIcon name="arrowR" width={17} height={17} />
								</button>
								{bookingDisabled && (
									<div className="rs-note" role="note" style={{ marginBottom: 0, color: "var(--zoco-gray-2)" }}>
										<RIcon name="info" width={15} height={15} />
										{ONLINE_BOOKING_DISABLED_MESSAGE}
									</div>
								)}
							</div>
						</div>
						<div className="policy-box">
							{bar.cancellationAllowed === false ||
							bar.cancellationAllowed === "false" ? (
								<>
									<div className="policy">
										<RIcon name="cal" width={16} height={16} />
										Las reservas se pueden realizar hasta con{" "}
										{bar.reservationWindowDays || 6}{" "}
										{bar.reservationWindowDays === 1 ? "día" : "días"} de
										antelación.
									</div>
									<div className="policy">
										<RIcon
											name="alert"
											width={16}
											height={16}
											style={{ color: "var(--zoco-danger)" }}
										/>
										Las reservas no son cancelables.
									</div>
								</>
							) : (
								<>
									<div className="policy">
										<RIcon name="cal" width={16} height={16} />
										Las reservas se pueden realizar hasta con{" "}
										{bar.reservationWindowDays || 6}{" "}
										{bar.reservationWindowDays === 1 ? "día" : "días"} de
										antelación.
									</div>
									<div className="policy">
										<RIcon name="shield" width={16} height={16} />
										Cancelación sin cargo hasta{" "}
										{bar.cancellationPolicyHours || 24} hs antes.
									</div>
								</>
							)}
							{bar.additionalNotes && (
								<div className="policy">
									<RIcon name="info" width={16} height={16} />
									{bar.additionalNotes}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<Modal
				show={showPaymentModal}
				onHide={() => {
					setShowPaymentModal(false);
					setPendingValues(null);
				}}
				centered
				backdrop="static"
				className="payment-modal"
			>
				<Modal.Header closeButton>
					<Modal.Title>Elegí cómo pagar</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-3">
						<label className="form-label">Tarjeta</label>
						<QtySelect
							className="payment-select"
							placeholder="Elegí una tarjeta"
							value={modalCard}
							onChange={(val) => setModalCard(val == null ? "" : String(val))}
							options={(cardOptions || []).map((opt) => ({
								value: opt.id,
								label: opt.label,
								disabled: opt.disabled,
							}))}
						/>
					</div>
					{selectedCardOption && (
						<div className="mb-3">
							<label className="form-label">Cuotas</label>
							<QtySelect
								className="payment-select"
								placeholder="Elegí la cantidad de cuotas"
								value="1"
								disabled={true}
								onChange={() => {}}
								options={[{ value: "1", label: "1 cuota (sin interés)" }]}
							/>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						onClick={() => {
							setShowPaymentModal(false);
							setPendingValues(null);
						}}
					>
						Cancelar
					</Button>
					<Button
						variant="primary"
						onClick={handleConfirmPaymentSelection}
						disabled={!selectedCardOption}
					>
						Continuar
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
}
